import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError, isAxiosError } from "axios";
import { z } from "zod";

export const TimekeepingActionEntityTypes = {
  TIMECLOCK_COMPLIANCE_VALIDATIONS: "timeclock-compliance-validations",
} as const;

const validateVideoRequestSchema = z.object({
  fileKey: z.string().min(1),
  bucketName: z.string().min(1),
  shiftId: z.string(),
});

const validateVideoResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(TimekeepingActionEntityTypes.TIMECLOCK_COMPLIANCE_VALIDATIONS),
    attributes: z.object({
      isValid: z.boolean(),
    }),
  }),
});

export type ValidateVideoRequest = z.infer<typeof validateVideoRequestSchema>;
export type ValidateVideoResponse = z.infer<typeof validateVideoResponseSchema>;

export function useValidateVideo(
  options: UseMutationOptions<{ isValid: boolean }, AxiosError, ValidateVideoRequest> = {}
): UseMutationResult<{ isValid: boolean }, AxiosError, ValidateVideoRequest> {
  return useMutation({
    mutationFn: async (data: ValidateVideoRequest) => {
      const { shiftId, ...requestData } = data;

      try {
        const response = await post({
          url: `${environmentConfig.REACT_APP_BASE_API_URL}/shifts/${shiftId}/timekeeping-compliance-validations`,
          data: requestData,
          requestSchema: validateVideoRequestSchema.omit({ shiftId: true }),
          responseSchema: validateVideoResponseSchema,
        });

        return response.data.data.attributes;
      } catch (error) {
        if (isAxiosError(error)) {
          const errorResponse = error.response?.data as { message: string };

          if (errorResponse?.message) {
            throw new Error(errorResponse.message);
          }
        }

        throw error;
      }
    },
    ...options,
  });
}
